// This is PoC code
import { EmojiEvents } from '@mui/icons-material';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { SvbrgContractComparatorOutput } from '@/generated/gql/graphql';
import { NULLISH_VALUE } from '~/common/constants';
import { I18nNamespace } from '~/common/types';

export type ContractComparisonResultProps = {
  result: SvbrgContractComparatorOutput;
};

export const ContractComparisonResult = ({ result }: ContractComparisonResultProps): ReactNode => {
  const { bestOption, rationale, contracts } = result;
  const { t } = useTranslation(I18nNamespace.intake, { keyPrefix: 'ContractComparisonResult' });

  return (
    <Box>
      {rationale && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>{t('recommendation')}</strong> {rationale}
          </Typography>
        </Paper>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell />
              {contracts.map((contract) => {
                const isWinner = bestOption === contract.fileId;
                return (
                  <TableCell key={contract.fileId} sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                    <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                      {isWinner && <EmojiEvents color="primary" />}
                      <Typography variant="subtitle2">{contract.contract.supplierName || contract.fileName}</Typography>
                    </Box>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('date')}</TableCell>
              {contracts.map((contract) => (
                <TableCell key={contract.fileId} align="center">
                  {contract.contract.date}
                </TableCell>
              ))}
            </TableRow>

            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('duration')}</TableCell>
              {contracts.map((contract) => (
                <TableCell key={contract.fileId} align="center">
                  {contract.contract.contractDuration
                    ? t('durationYears', { count: contract.contract.contractDuration })
                    : NULLISH_VALUE}
                </TableCell>
              ))}
            </TableRow>

            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('products')}</TableCell>
              {contracts.map((contract) => (
                <TableCell key={contract.fileId} align="center">
                  <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                    {contract.contract.products?.map((product) => (
                      <li key={product.name}>{product.name}</li>
                    ))}
                  </Box>
                </TableCell>
              ))}
            </TableRow>

            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('qualityStandards')}</TableCell>
              {contracts.map((contract) => (
                <TableCell key={contract.fileId} align="center">
                  {contract.contract.qualityStandards?.length ? (
                    <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                      {contract.contract.qualityStandards.map((standard) => (
                        <li key={standard}>{standard}</li>
                      ))}
                    </Box>
                  ) : (
                    NULLISH_VALUE
                  )}
                </TableCell>
              ))}
            </TableRow>

            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('deliveryLocation')}</TableCell>
              {contracts.map((contract) => (
                <TableCell key={contract.fileId} align="center">
                  {contract.contract.ordering?.deliveryLocation || NULLISH_VALUE}
                </TableCell>
              ))}
            </TableRow>

            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('deliveryTime')}</TableCell>
              {contracts.map((contract) => (
                <TableCell key={contract.fileId} align="center">
                  {contract.contract.ordering?.deliveryTimeDays
                    ? t('deliveryTimeDays', { count: contract.contract.ordering.deliveryTimeDays })
                    : NULLISH_VALUE}
                </TableCell>
              ))}
            </TableRow>

            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('paymentTerms')}</TableCell>
              {contracts.map((contract) => (
                <TableCell key={contract.fileId} align="center">
                  <Box>
                    {contract.contract.paymentTerms?.dueDays && (
                      <div>{t('paymentDue', { count: contract.contract.paymentTerms.dueDays })}</div>
                    )}
                    {contract.contract.paymentTerms?.earlyPaymentDiscountPercent && (
                      <div>
                        {t('earlyDiscount', { percent: contract.contract.paymentTerms.earlyPaymentDiscountPercent })}
                      </div>
                    )}
                    {!contract.contract.paymentTerms?.dueDays &&
                      !contract.contract.paymentTerms?.earlyPaymentDiscountPercent &&
                      NULLISH_VALUE}
                  </Box>
                </TableCell>
              ))}
            </TableRow>

            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('qualityControl')}</TableCell>
              {contracts.map((contract) => (
                <TableCell key={contract.fileId} align="center">
                  <Box>
                    {contract.contract.qualityControl?.warrantyYears && (
                      <div>{t('warranty', { count: contract.contract.qualityControl.warrantyYears })}</div>
                    )}
                    {contract.contract.qualityControl?.auditFrequency && (
                      <div>{t('audit', { frequency: contract.contract.qualityControl.auditFrequency })}</div>
                    )}
                    {!contract.contract.qualityControl?.warrantyYears &&
                      !contract.contract.qualityControl?.auditFrequency &&
                      NULLISH_VALUE}
                  </Box>
                </TableCell>
              ))}
            </TableRow>

            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('termination')}</TableCell>
              {contracts.map((contract) => (
                <TableCell key={contract.fileId} align="center">
                  {contract.contract.termination?.standardNoticeDays
                    ? t('terminationNotice', { count: contract.contract.termination.standardNoticeDays })
                    : NULLISH_VALUE}
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
